import { useState, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AlertCircle, CreditCard } from 'lucide-react';

interface ProcessingHistoryStepProps {
  onSubmit: (data: any) => void;
  onPrevious?: () => void;
  initialData?: any;
  shouldShowField?: (fieldPath: string) => boolean;
  isFieldRequired?: (fieldPath: string) => boolean;
  getFieldDefaultValue?: (fieldPath: string, includeWhenVisible?: boolean) => any;
}

export function ProcessingHistoryStep({ 
  onSubmit, 
  onPrevious, 
  initialData,
  shouldShowField = () => true,
  isFieldRequired = () => true,
  getFieldDefaultValue = () => undefined
}: ProcessingHistoryStepProps) {
  const [serverError, setServerError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Create dynamic schema based on conditional requirements
  const processingHistorySchema = useMemo(() => {
    const baseSchema: any = {};

    if (shouldShowField('processingHistory.isCurrentlyProcessing')) {
      baseSchema.isCurrentlyProcessing = isFieldRequired('processingHistory.isCurrentlyProcessing')
        ? z.enum(['yes', 'no'])
        : z.enum(['yes', 'no']).optional();
    }

    if (shouldShowField('processingHistory.monthlyVolume')) {
      baseSchema.monthlyVolume = isFieldRequired('processingHistory.monthlyVolume')
        ? z.number().min(0, 'Monthly volume must be 0 or greater')
        : z.number().min(0).optional();
    }

    if (shouldShowField('processingHistory.averageTicket')) {
      baseSchema.averageTicket = isFieldRequired('processingHistory.averageTicket')
        ? z.number().min(0, 'Average ticket must be 0 or greater')
        : z.number().min(0).optional();
    }

    if (shouldShowField('processingHistory.highTicket')) {
      baseSchema.highTicket = isFieldRequired('processingHistory.highTicket')
        ? z.number().min(0, 'High ticket must be 0 or greater')
        : z.number().min(0).optional();
    }

    if (shouldShowField('processingHistory.salesDeliveryDays')) {
      baseSchema.salesDeliveryDays = isFieldRequired('processingHistory.salesDeliveryDays')
        ? z.number().min(0, 'Average delivery days must be 0 or greater')
        : z.number().min(0).optional();
    }

    if (shouldShowField('processingHistory.whenIsCustomerCharged')) {
      baseSchema.whenIsCustomerCharged = isFieldRequired('processingHistory.whenIsCustomerCharged')
        ? z.enum(['order', 'delivery', 'shipment'])
        : z.enum(['order', 'delivery', 'shipment']).optional();
    }

    if (shouldShowField('processingHistory.orderTurnaroundTime')) {
      baseSchema.orderTurnaroundTime = isFieldRequired('processingHistory.orderTurnaroundTime')
        ? z.number().min(0, 'Turnaround time must be 0 or greater')
        : z.number().min(0).optional();
    }

    if (shouldShowField('processingHistory.advancedDeposits')) {
      baseSchema.advancedDeposits = isFieldRequired('processingHistory.advancedDeposits')
        ? z.enum(['yes', 'no'])
        : z.enum(['yes', 'no']).optional();
    }

    if (shouldShowField('processingHistory.depositPct')) {
      baseSchema.depositPct = isFieldRequired('processingHistory.depositPct')
        ? z.number().min(0, 'Deposit percentage must be 0 or greater')
        : z.number().min(0).optional();
    }

    if (shouldShowField('processingHistory.currentProcessor')) {
      baseSchema.currentProcessor = z.string().optional(); // Always optional since it's conditionally shown
    }

    // Legacy field support for backwards compatibility
    if (shouldShowField('processingHistory.cardPresentPercentage')) {
      baseSchema.cardPresentPercentage = isFieldRequired('processingHistory.cardPresentPercentage')
        ? z.number().min(0).max(100, 'Percentage must be between 0 and 100')
        : z.number().min(0).max(100).optional();
    }

    if (shouldShowField('processingHistory.ecommercePercentage')) {
      baseSchema.ecommercePercentage = isFieldRequired('processingHistory.ecommercePercentage')
        ? z.number().min(0).max(100, 'Percentage must be between 0 and 100')
        : z.number().min(0).max(100).optional();
    }

    if (shouldShowField('processingHistory.acceptOfflineCards')) {
      baseSchema.acceptOfflineCards = z.boolean().optional();
    }

    if (shouldShowField('processingHistory.retailPercentage')) {
      baseSchema.retailPercentage = isFieldRequired('processingHistory.retailPercentage')
        ? z.number().min(0).max(100, 'Percentage must be between 0 and 100')
        : z.number().min(0).max(100).optional();
    }

    if (shouldShowField('processingHistory.onlinePercentage')) {
      baseSchema.onlinePercentage = isFieldRequired('processingHistory.onlinePercentage')
        ? z.number().min(0).max(100, 'Percentage must be between 0 and 100')
        : z.number().min(0).max(100).optional();
    }

    if (shouldShowField('processingHistory.b2bPercentage')) {
      baseSchema.b2bPercentage = isFieldRequired('processingHistory.b2bPercentage')
        ? z.number().min(0).max(100, 'Percentage must be between 0 and 100')
        : z.number().min(0).max(100).optional();
    }

    if (shouldShowField('processingHistory.b2cPercentage')) {
      baseSchema.b2cPercentage = isFieldRequired('processingHistory.b2cPercentage')
        ? z.number().min(0).max(100, 'Percentage must be between 0 and 100')
        : z.number().min(0).max(100).optional();
    }

    if (shouldShowField('processingHistory.bankruptcy')) {
      baseSchema.bankruptcy = z.boolean().optional();
    }

    if (shouldShowField('processingHistory.hasBeenTerminated')) {
      baseSchema.hasBeenTerminated = isFieldRequired('processingHistory.hasBeenTerminated')
        ? z.enum(['yes', 'no'])
        : z.enum(['yes', 'no']).optional();
    }

    if (shouldShowField('processingHistory.terminationExplanation')) {
      baseSchema.terminationExplanation = z.string().optional(); // Always optional since it's conditionally shown
    }

    return z.object(baseSchema);
  }, [shouldShowField, isFieldRequired]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm({
    resolver: zodResolver(processingHistorySchema),
    defaultValues: {
      isCurrentlyProcessing: initialData?.processingHistory?.isCurrentlyProcessing || 
        getFieldDefaultValue('processingHistory.isCurrentlyProcessing') || 'no',
      monthlyVolume: initialData?.processingHistory?.monthlyVolume || 
        getFieldDefaultValue('processingHistory.monthlyVolume') || 0,
      averageTicket: initialData?.processingHistory?.averageTicket || 
        getFieldDefaultValue('processingHistory.averageTicket') || 0,
      highTicket: initialData?.processingHistory?.highTicket || 
        getFieldDefaultValue('processingHistory.highTicket') || 0,
      salesDeliveryDays: initialData?.processingHistory?.salesDeliveryDays ||
        getFieldDefaultValue('processingHistory.salesDeliveryDays') || 0,
      whenIsCustomerCharged: initialData?.processingHistory?.whenIsCustomerCharged ||
        getFieldDefaultValue('processingHistory.whenIsCustomerCharged') || 'order',
      orderTurnaroundTime: initialData?.processingHistory?.orderTurnaroundTime ||
        getFieldDefaultValue('processingHistory.orderTurnaroundTime') || 7,
      advancedDeposits: initialData?.processingHistory?.advancedDeposits ||
        getFieldDefaultValue('processingHistory.advancedDeposits') || 'no',
      depositPct: initialData?.processingHistory?.depositPct ||
        getFieldDefaultValue('processingHistory.depositPct') || 0,
      currentProcessor: initialData?.processingHistory?.currentProcessor || 
        getFieldDefaultValue('processingHistory.currentProcessor') || '',
      // Legacy fields for backwards compatibility
      cardPresentPercentage: initialData?.processingHistory?.cardPresentPercentage || 
        getFieldDefaultValue('processingHistory.cardPresentPercentage') || 0,
      ecommercePercentage: initialData?.processingHistory?.ecommercePercentage || 
        getFieldDefaultValue('processingHistory.ecommercePercentage') || 0,
      acceptOfflineCards: initialData?.processingHistory?.acceptOfflineCards || 
        getFieldDefaultValue('processingHistory.acceptOfflineCards') || false,
      retailPercentage: initialData?.processingHistory?.retailPercentage || 
        getFieldDefaultValue('processingHistory.retailPercentage') || 90,
      onlinePercentage: initialData?.processingHistory?.onlinePercentage || 
        getFieldDefaultValue('processingHistory.onlinePercentage') || 10,
      b2bPercentage: initialData?.processingHistory?.b2bPercentage || 
        getFieldDefaultValue('processingHistory.b2bPercentage') || 50,
      b2cPercentage: initialData?.processingHistory?.b2cPercentage || 
        getFieldDefaultValue('processingHistory.b2cPercentage') || 50,
      bankruptcy: initialData?.processingHistory?.bankruptcy || 
        getFieldDefaultValue('processingHistory.bankruptcy') || false,
      hasBeenTerminated: initialData?.processingHistory?.hasBeenTerminated || 
        getFieldDefaultValue('processingHistory.hasBeenTerminated') || 'no',
      terminationExplanation: initialData?.processingHistory?.terminationExplanation || 
        getFieldDefaultValue('processingHistory.terminationExplanation') || '',
    },
    mode: 'onBlur',
  });

  const isCurrentlyProcessing = watch('isCurrentlyProcessing');
  const hasBeenTerminated = watch('hasBeenTerminated');
  const acceptOfflineCards = watch('acceptOfflineCards');
  const retailPercentage = watch('retailPercentage');
  const onlinePercentage = watch('onlinePercentage');
  const advancedDeposits = watch('advancedDeposits');

  useEffect(() => {
    if (advancedDeposits !== 'yes') {
      setValue('depositPct', 0, { shouldValidate: true });
    }
  }, [advancedDeposits, setValue]);

  // Debug logging (reduced for performance)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 ProcessingHistoryStep - Field visibility check');
    }
  }, [shouldShowField]);

  const onFormSubmit = async (data: any) => {
    try {
      setIsSubmitting(true);
      setServerError(null);
      
      // Merge in default values for disabled fields
      const submissionData = { ...data };
      
      // Check each field and if it's disabled, use the default value
      const fields = [
        'isCurrentlyProcessing',
        'monthlyVolume', 
        'averageTicket',
        'highTicket',
        'salesDeliveryDays',
        'whenIsCustomerCharged',
        'orderTurnaroundTime',
        'advancedDeposits',
        'depositPct',
        'currentProcessor',
        'cardPresentPercentage',
        'ecommercePercentage',
        'acceptOfflineCards',
        'retailPercentage',
        'onlinePercentage',
        'b2bPercentage',
        'b2cPercentage',
        'bankruptcy',
        'hasBeenTerminated',
        'terminationExplanation'
      ];
      
      fields.forEach(field => {
        const fieldPath = `processingHistory.${field}`;
        if (!shouldShowField(fieldPath)) {
          const defaultValue = getFieldDefaultValue(fieldPath);
          if (defaultValue !== undefined) {
            submissionData[field] = defaultValue;
          }
        }
      });

      const normalizedChargeTiming = (submissionData.whenIsCustomerCharged || 'order').toString().toLowerCase();
      submissionData.whenIsCustomerCharged = ['order', 'delivery', 'shipment'].includes(normalizedChargeTiming)
        ? normalizedChargeTiming
        : 'order';

      const normalizedTurnaround = Number(submissionData.orderTurnaroundTime ?? 0);
      submissionData.orderTurnaroundTime = Number.isFinite(normalizedTurnaround) && normalizedTurnaround >= 0
        ? normalizedTurnaround
        : 0;

      submissionData.advancedDeposits = submissionData.advancedDeposits === 'yes' ? 'yes' : 'no';

      const normalizedDeposit = Number(submissionData.depositPct ?? 0);
      submissionData.depositPct = Number.isFinite(normalizedDeposit) && normalizedDeposit >= 0
        ? Math.min(normalizedDeposit, 100)
        : 0;

      if (submissionData.advancedDeposits !== 'yes') {
        submissionData.depositPct = 0;
      }

      // Conditional validation: Only require currentProcessor if currently processing is 'yes'
      if (submissionData.isCurrentlyProcessing === 'yes' && shouldShowField('processingHistory.currentProcessor')) {
        if (!submissionData.currentProcessor || submissionData.currentProcessor.trim() === '') {
          setServerError('Current Payment Provider is required when you are currently processing credit card payments');
          return;
        }
      }
      
      // Conditional validation: Only require terminationExplanation if hasBeenTerminated is 'yes'
      if (submissionData.hasBeenTerminated === 'yes' && shouldShowField('processingHistory.terminationExplanation')) {
        if (!submissionData.terminationExplanation || submissionData.terminationExplanation.trim() === '') {
          setServerError('Please explain the circumstances when you have had issues with previous payment providers');
          return;
        }
      }
      
      // Auto-calculate online percentage from retail percentage when offline cards is used
      if (submissionData.acceptOfflineCards && submissionData.retailPercentage !== undefined) {
        submissionData.onlinePercentage = 100 - Number(submissionData.retailPercentage);
      }
      
      // Auto-calculate B2C from B2B percentage 
      if (submissionData.b2bPercentage !== undefined) {
        submissionData.b2cPercentage = 100 - Number(submissionData.b2bPercentage);
      }
      
      // Validate percentages - only if offline cards is enabled
      if (submissionData.acceptOfflineCards) {
        const retail = Number(submissionData.retailPercentage) || 0;
        const online = Number(submissionData.onlinePercentage) || 0;
        
        if (retail + online !== 100) {
          setServerError('Retail and online percentages must add up to 100%');
          return;
        }
        
        // Ensure we have at least some retail percentage if they accept offline cards
        if (retail === 0) {
          setServerError('Retail percentage must be greater than 0% if you accept offline cards');
          return;
        }
      }
      
      await onSubmit({ processingHistory: submissionData });
    } catch (error) {
      console.error('Submission error:', error);
      setServerError('An error occurred while saving your information');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="h-5 w-5" />
          Payment Experience
        </CardTitle>
        <CardDescription>
          Tell us about your business and expected sales.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        {serverError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{serverError}</AlertDescription>
          </Alert>
        )}

        {/* Currently Processing */}
        {shouldShowField('processingHistory.isCurrentlyProcessing') && (
          <div className="space-y-3">
            <Label>
              Are you currently processing credit card payments?
              {isFieldRequired('processingHistory.isCurrentlyProcessing') && (
                <span className="text-red-500 ml-1">*</span>
              )}
            </Label>
            <RadioGroup
              defaultValue={watch('isCurrentlyProcessing')}
              onValueChange={(value) => setValue('isCurrentlyProcessing', value)}
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="yes" id="processing-yes" />
                <Label htmlFor="processing-yes">Yes</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="no" id="processing-no" />
                <Label htmlFor="processing-no">No</Label>
              </div>
            </RadioGroup>
          </div>
        )}

        {/* Current Processor */}
        {isCurrentlyProcessing === 'yes' && shouldShowField('processingHistory.currentProcessor') && (
          <div className="space-y-2">
            <Label htmlFor="currentProcessor">
              Current Payment Provider
              {isFieldRequired('processingHistory.currentProcessor') && (
                <span className="text-red-500 ml-1">*</span>
              )}
            </Label>
            <Input
              id="currentProcessor"
              {...register('currentProcessor')}
              placeholder="e.g., Stripe, Square, PayPal"
            />
          </div>
        )}

        {/* Volume Information */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {shouldShowField('processingHistory.monthlyVolume') && (
            <div className="space-y-2">
              <Label htmlFor="monthlyVolume">
                Expected Monthly Sales ($)
                {isFieldRequired('processingHistory.monthlyVolume') && (
                  <span className="text-red-500 ml-1">*</span>
                )}
              </Label>
              <Input
                id="monthlyVolume"
                type="number"
                {...register('monthlyVolume', { valueAsNumber: true })}
                placeholder="0"
                min="0"
                onFocus={(e) => (e.target as HTMLInputElement).select()} // Select all text when focused
                onClick={(e) => (e.target as HTMLInputElement).select()}  // Select all text when clicked
              />
              {errors.monthlyVolume && (
                <p className="text-sm text-red-600">{errors.monthlyVolume.message as string}</p>
              )}
            </div>
          )}

          {shouldShowField('processingHistory.averageTicket') && (
            <div className="space-y-2">
              <Label htmlFor="averageTicket">
                Average Invoice Amount ($)
                {isFieldRequired('processingHistory.averageTicket') && (
                  <span className="text-red-500 ml-1">*</span>
                )}
              </Label>
              <Input
                id="averageTicket"
                type="number"
                {...register('averageTicket', { valueAsNumber: true })}
                placeholder="0"
                min="0"
              />
              {errors.averageTicket && (
                <p className="text-sm text-red-600">{errors.averageTicket.message as string}</p>
              )}
            </div>
          )}

          {shouldShowField('processingHistory.highTicket') && (
            <div className="space-y-2">
              <Label htmlFor="highTicket">
                Largest Invoice Amount ($)
                {isFieldRequired('processingHistory.highTicket') && (
                  <span className="text-red-500 ml-1">*</span>
                )}
              </Label>
              <Input
                id="highTicket"
                type="number"
                {...register('highTicket', { valueAsNumber: true })}
                placeholder="0"
                min="0"
              />
              {errors.highTicket && (
                <p className="text-sm text-red-600">{errors.highTicket.message as string}</p>
              )}
            </div>
          )}
        {shouldShowField('processingHistory.salesDeliveryDays') && (
          <div className="space-y-2">
            <Label htmlFor="salesDeliveryDays">
              Average Delivery Time (days)
              {isFieldRequired('processingHistory.salesDeliveryDays') && (
                <span className="text-red-500 ml-1">*</span>
              )}
            </Label>
            <Input
              id="salesDeliveryDays"
              type="number"
              {...register('salesDeliveryDays', { valueAsNumber: true })}
              placeholder="0"
              min="0"
            />
            {errors.salesDeliveryDays && (
              <p className="text-sm text-red-600">{errors.salesDeliveryDays.message as string}</p>
            )}
          </div>
        )}
        {shouldShowField('processingHistory.orderTurnaroundTime') && (
          <div className="space-y-2">
            <Label htmlFor="orderTurnaroundTime">
              Avg Order Turnaround (days)
              {isFieldRequired('processingHistory.orderTurnaroundTime') && (
                <span className="text-red-500 ml-1">*</span>
              )}
            </Label>
            <Input
              id="orderTurnaroundTime"
              type="number"
              {...register('orderTurnaroundTime', { valueAsNumber: true })}
              placeholder="7"
              min="0"
            />
            {errors.orderTurnaroundTime && (
              <p className="text-sm text-red-600">{errors.orderTurnaroundTime.message as string}</p>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {shouldShowField('processingHistory.whenIsCustomerCharged') && (
          <div className="space-y-2">
            <Label htmlFor="whenIsCustomerCharged">
              When do you charge the credit card?
              {isFieldRequired('processingHistory.whenIsCustomerCharged') && (
                <span className="text-red-500 ml-1">*</span>
              )}
            </Label>
            <select
              id="whenIsCustomerCharged"
              {...register('whenIsCustomerCharged')}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="order">At order</option>
              <option value="delivery">At delivery</option>
              <option value="shipment">At shipment</option>
            </select>
          </div>
        )}
        {shouldShowField('processingHistory.advancedDeposits') && (
          <div className="space-y-2">
            <Label htmlFor="advancedDeposits">
              Collect advanced deposits?
              {isFieldRequired('processingHistory.advancedDeposits') && (
                <span className="text-red-500 ml-1">*</span>
              )}
            </Label>
            <select
              id="advancedDeposits"
              {...register('advancedDeposits')}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="no">No</option>
              <option value="yes">Yes</option>
            </select>
          </div>
        )}
      </div>

      {shouldShowField('processingHistory.depositPct') && advancedDeposits === 'yes' && (
        <div className="space-y-2">
          <Label htmlFor="depositPct">
            Deposit percentage (%)
            {isFieldRequired('processingHistory.depositPct') && (
              <span className="text-red-500 ml-1">*</span>
            )}
          </Label>
          <Input
            id="depositPct"
            type="number"
            {...register('depositPct', { valueAsNumber: true })}
            min="0"
            max="100"
            placeholder="0"
          />
          {errors.depositPct && (
            <p className="text-sm text-red-600">{errors.depositPct.message as string}</p>
          )}
        </div>
      )}

        {/* Enhanced Processing Type Workflow - Accept Offline Cards Question */}
        {shouldShowField('processingHistory.acceptOfflineCards') && (
          <div className="space-y-4">
            <div className="space-y-3">
              <Label>Credit Card Processing Setup</Label>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="acceptOfflineCards"
                  checked={acceptOfflineCards}
                  onCheckedChange={(checked) => {
                    setValue('acceptOfflineCards', !!checked);
                    // Auto-set initial percentages when enabling offline cards
                    if (checked) {
                      setValue('retailPercentage', 90);
                      setValue('onlinePercentage', 10);
                    }
                  }}
                />
                <Label htmlFor="acceptOfflineCards" className="text-sm font-normal">
                  Do you need to accept credit cards offline? (in-person/retail transactions)
                </Label>
              </div>
            </div>

            {/* Show retail/online split ONLY if offline cards is enabled */}
            {acceptOfflineCards && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 border rounded-lg bg-gray-50">
                <div className="space-y-2">
                  <Label htmlFor="retailPercentage">
                    Retail (In-Person) %
                    {isFieldRequired('processingHistory.retailPercentage') && (
                      <span className="text-red-500 ml-1">*</span>
                    )}
                  </Label>
                  <Input
                    id="retailPercentage"
                    type="number"
                    value={retailPercentage || ''}
                    onChange={(e) => {
                      let value = Number(e.target.value) || 0;
                      // Prevent values above 100
                      if (value > 100) value = 100;
                      // Prevent negative values
                      if (value < 0) value = 0;
                      setValue('retailPercentage', value);
                      setValue('onlinePercentage', 100 - value);
                    }}
                    min="0"
                    max="100"
                    placeholder="90"
                  />
                  {errors.retailPercentage && (
                    <p className="text-sm text-red-600">{errors.retailPercentage.message as string}</p>
                  )}
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="onlinePercentage">
                    Online Sales %
                    {isFieldRequired('processingHistory.onlinePercentage') && (
                      <span className="text-red-500 ml-1">*</span>
                    )}
                  </Label>
                  <Input
                    id="onlinePercentage"
                    type="number"
                    value={onlinePercentage || ''}
                    onChange={(e) => {
                      let value = Number(e.target.value) || 0;
                      // Prevent values above 100
                      if (value > 100) value = 100;
                      // Prevent negative values
                      if (value < 0) value = 0;
                      setValue('onlinePercentage', value);
                      setValue('retailPercentage', 100 - value);
                    }}
                    min="0"
                    max="100"
                    placeholder="10"
                  />
                  {errors.onlinePercentage && (
                    <p className="text-sm text-red-600">{errors.onlinePercentage.message as string}</p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}


        {/* Bankruptcy Question */}
        {shouldShowField('processingHistory.bankruptcy') && (
          <div className="space-y-3">
            <Label>Risk Assessment</Label>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="bankruptcy"
                checked={watch('bankruptcy')}
                onCheckedChange={(checked) => setValue('bankruptcy', !!checked)}
              />
              <Label htmlFor="bankruptcy" className="text-sm font-normal">
                Have you ever filed for bankruptcy?
                {isFieldRequired('processingHistory.bankruptcy') && (
                  <span className="text-red-500 ml-1">*</span>
                )}
              </Label>
            </div>
          </div>
        )}

        {/* Termination History */}
        {shouldShowField('processingHistory.hasBeenTerminated') && (
          <div className="space-y-3">
            <Label>
              Have you had any issues with previous payment providers?
              {isFieldRequired('processingHistory.hasBeenTerminated') && (
                <span className="text-red-500 ml-1">*</span>
              )}
            </Label>
            <RadioGroup
              defaultValue={watch('hasBeenTerminated')}
              onValueChange={(value) => setValue('hasBeenTerminated', value)}
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="yes" id="terminated-yes" />
                <Label htmlFor="terminated-yes">Yes</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="no" id="terminated-no" />
                <Label htmlFor="terminated-no">No</Label>
              </div>
            </RadioGroup>
          </div>
        )}

        {/* Termination Explanation */}
        {hasBeenTerminated === 'yes' && shouldShowField('processingHistory.terminationExplanation') && (
          <div className="space-y-2">
            <Label htmlFor="terminationExplanation">
              Please explain the circumstances
              {isFieldRequired('processingHistory.terminationExplanation') && (
                <span className="text-red-500 ml-1">*</span>
              )}
            </Label>
            <Textarea
              id="terminationExplanation"
              {...register('terminationExplanation')}
              placeholder="Provide details about the termination..."
              rows={3}
            />
            {errors.terminationExplanation && (
              <p className="text-sm text-red-600">{errors.terminationExplanation.message as string}</p>
            )}
          </div>
        )}

        {/* Form Actions */}
        <div className="flex justify-between pt-6">
          {onPrevious && (
            <Button type="button" variant="outline" onClick={onPrevious}>
              Previous
            </Button>
          )}
          <Button type="submit" disabled={isSubmitting} className="ml-auto">
            {isSubmitting ? 'Saving...' : 'Save & Continue'}
          </Button>
        </div>
      </CardContent>
    </form>
  );
}
